import { RefreshCwIcon } from "lucide-react";
import { useState } from "react";

import {
  enumerateDays,
  enumerateHourStarts,
  formatDateTimeShort,
  formatDayShort,
  makeWindow,
} from "@cmmd-forge/shared/usageFormat";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePublishBreadcrumbDetail } from "../breadcrumb/globalBreadcrumbStore";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "../../lib/touchTarget";
import { useUsage } from "../../usage/useUsage";
import { useUsageWindowSelection } from "../../usage/useUsageWindowSelection";
import { useUsageEnvironmentLabels } from "../../usage/useUsageEnvironmentLabels";
import { AppTopBarActions } from "../appTopBarActionSlot";
import { Button } from "../ui/button";
import { Toggle, ToggleGroup } from "../ui/toggle-group";
import type { UsageBreakdownMode } from "./UsageBreakdown";
import { UsageSkeleton, UsageSourceStrip } from "./UsageCoverage";
import { UsageEstimateLoaded } from "./UsageEstimateLoaded";
import { UsageEstimateNotice } from "./UsageNotice";
import type { UsageChartMetric } from "./UsageProviderChart";
import { readUsagePagePreferences, saveUsagePagePreferences } from "./usagePagePreferences";
import { providersWithUsage } from "./usageProviders";

const WINDOW_OPTIONS = [
  { days: 1, label: "Past 24h" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

interface UsageControlBarProps {
  readonly window: ReturnType<typeof makeWindow>;
  readonly windowDays: number;
  readonly isPast24Hours: boolean;
  readonly metric: UsageChartMetric;
  readonly onMetricChange: (metric: UsageChartMetric) => void;
  readonly onSelect: (days: number) => void;
  readonly onRefresh: () => void;
}

/** The window this tab is reading, as prose. */
function resolveRangeLabel(window: ReturnType<typeof makeWindow>, isPast24Hours: boolean): string {
  const { sinceTime, untilTime, timeZone } = window;
  return isPast24Hours && sinceTime !== undefined && untilTime !== undefined
    ? `${formatDateTimeShort(sinceTime, timeZone)} to ${formatDateTimeShort(untilTime, timeZone)}`
    : `${formatDayShort(window.sinceDay)} to ${formatDayShort(window.untilDay)}`;
}

/**
 * What is measured, over how long, and a refresh.
 *
 * Split out from the row that used to own it so the same cluster can render in
 * two places without being declared twice: the global top bar from `lg` up, and
 * the tab body below it.
 */
function UsageWindowControls({
  windowDays,
  metric,
  onMetricChange,
  onSelect,
  onRefresh,
}: Omit<UsageControlBarProps, "window" | "isPast24Hours">) {
  return (
    <>
      <ToggleGroup
        aria-label="Usage metric"
        variant="segmented"
        value={[metric]}
        onValueChange={(next) => {
          const value = next[0];
          if (value === "cost" || value === "tokens") onMetricChange(value);
        }}
      >
        {(["cost", "tokens"] as const).map((option) => (
          <Toggle key={option} value={option}>
            {option === "cost" ? "Cost" : "Tokens"}
          </Toggle>
        ))}
      </ToggleGroup>
      {/* Grouped so the refresh wraps with the window it refreshes, rather
          than stranding itself on a row of its own on a narrow screen. */}
      <div className="flex items-center gap-2">
        <ToggleGroup
          aria-label="Usage period"
          variant="segmented"
          value={[String(windowDays)]}
          onValueChange={(next) => {
            const value = next[0];
            if (value !== undefined) onSelect(Number(value));
          }}
        >
          {WINDOW_OPTIONS.map((option) => (
            <Toggle key={option.days} value={String(option.days)}>
              {option.label}
            </Toggle>
          ))}
        </ToggleGroup>
        <Button
          size="icon"
          variant="ghost"
          className={RESPONSIVE_ICON_BUTTON_CLASS}
          onClick={onRefresh}
          aria-label="Refresh usage"
        >
          <RefreshCwIcon className="size-3.5" />
        </Button>
      </div>
    </>
  );
}

/**
 * Range label on the left; the window controls on the right.
 *
 * The below-`lg` placement. t3code carries these in the page topbar at every
 * width, but Forge's bar is 56px of touch chrome on a phone and already holds
 * the sidebar trigger and the breadcrumb, so three control groups cannot go
 * there — t3code itself collapses them to compact Selects below `lg` for the
 * same reason. Above `lg` this row is not rendered at all; see
 * `UsageEstimateTab`.
 */
function UsageControlBar({
  window,
  windowDays,
  isPast24Hours,
  metric,
  onMetricChange,
  onSelect,
  onRefresh,
}: UsageControlBarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      <p className="text-sm text-muted-foreground">{resolveRangeLabel(window, isPast24Hours)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <UsageWindowControls
          windowDays={windowDays}
          metric={metric}
          onMetricChange={onMetricChange}
          onSelect={onSelect}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
}

/**
 * Estimated API-equivalent value of every recorded turn.
 *
 * Owns the window, the two display toggles, and the merged totals every
 * renderer below reads from. The window is this tab's alone: the plan tab is
 * bound to the subscription period instead.
 */
export function UsageEstimateTab() {
  const { windowDays, window, isPast24Hours, resolution, selectWindow, refreshWindow } =
    useUsageWindowSelection();
  const [metric, setMetric] = useState<UsageChartMetric>(() => readUsagePagePreferences().metric);
  // The metric remembers across visits; the window's persistence lives in
  // the selection hook, and each writer merges over a fresh read so the two
  // axes never overwrite each other.
  const changeMetric = (next: UsageChartMetric) => {
    setMetric(next);
    saveUsagePagePreferences({ ...readUsagePagePreferences(), metric: next });
  };
  const [breakdown, setBreakdown] = useState<UsageBreakdownMode>("model");
  // From `lg` the controls belong to the global bar, which is where t3code
  // carries them and where the bar's right zone is otherwise empty on this
  // route. Below it the bar is touch chrome with no room, so the in-body row
  // stays. Only ever one of the two renders, so there is never a second
  // `Usage metric` group in the tree.
  const hoistControlsToTopBar = useMediaQuery("lg");
  // The window belongs to the breadcrumb, where upstream renders it: it names
  // what the numbers cover, which is a qualifier on the page rather than one of
  // the controls beside it. Published as a string so the bar's selector stays
  // primitive; it changes on every period click.
  usePublishBreadcrumbDetail(resolveRangeLabel(window, isPast24Hours));
  const { merged, sources, isPending, isPartial, refresh } = useUsage(window);
  const labelForEnvironment = useUsageEnvironmentLabels(sources);

  // Hold the content until every source is terminal. Rendering merged totals
  // while devices are still answering makes every number on the page jump as
  // each one lands.
  const settling = isPending || isPartial;

  const days = enumerateDays(window.sinceDay, window.untilDay);
  const hours =
    window.sinceTime === undefined || window.untilTime === undefined
      ? []
      : enumerateHourStarts(window.sinceTime, window.untilTime);
  // Newest first: the window can run 90 periods, so the interesting end belongs
  // at the top of the table.
  const breakdownPeriods = (isPast24Hours ? merged.hourly : merged.daily).toReversed();

  // A fixed reading order filtered to the lanes that actually reported, so
  // flipping the metric never reshuffles the rows or the chart series.
  const activeProviders = providersWithUsage(merged.providers);

  return (
    <div className="flex flex-col gap-8">
      <UsageEstimateNotice />

      {hoistControlsToTopBar ? (
        <AppTopBarActions>
          <UsageWindowControls
            windowDays={windowDays}
            metric={metric}
            onMetricChange={changeMetric}
            onSelect={selectWindow}
            onRefresh={() => refreshWindow(refresh)}
          />
        </AppTopBarActions>
      ) : (
        <UsageControlBar
          window={window}
          windowDays={windowDays}
          isPast24Hours={isPast24Hours}
          metric={metric}
          onMetricChange={changeMetric}
          onSelect={selectWindow}
          onRefresh={() => refreshWindow(refresh)}
        />
      )}

      {settling ? (
        <>
          {sources.length > 1 ? <UsageSourceStrip sources={sources} /> : null}
          <UsageSkeleton resolution={resolution} />
        </>
      ) : (
        <UsageEstimateLoaded
          merged={merged}
          sources={sources}
          labelForEnvironment={labelForEnvironment}
          metric={metric}
          breakdown={breakdown}
          onBreakdownChange={setBreakdown}
          activeProviders={activeProviders}
          days={days}
          hours={hours}
          breakdownPeriods={breakdownPeriods}
          isPast24Hours={isPast24Hours}
          resolution={resolution}
          window={window}
        />
      )}
    </div>
  );
}
